import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  makeStyles,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import LinkIcon from "@material-ui/icons/Link";
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import CloudOffIcon from "@material-ui/icons/CloudOff";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { cartaoAstro, tituloSecaoAstro } from "../../utils/estiloAstro";
import ModalAstro from "../ModalAstro";


const MASCARA = "••••••••";

const useStyles = makeStyles(theme => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(148,163,184,.14)" : "#e3ece6";
  return {
  cabecalho: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
    flexWrap: "wrap",
    gap: theme.spacing(2)
  },
  tituloPainel: {
    fontWeight: 800,
    fontSize: "1.25rem",
    lineHeight: 1.2
  },
  // resumo (ativos / desligados / total) + busca
  barraResumo: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: theme.spacing(1)
  },
  estatistica: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px 8px 10px",
    borderRadius: 14,
    border: `1px solid ${borda}`,
    backgroundColor: theme.palette.background.paper,
    animation: "iaSubir .4s ease backwards"
  },
  estatisticaIcone: {
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiSvgIcon-root": { fontSize: 18 }
  },
  estatisticaNumero: {
    fontWeight: 800,
    fontSize: "1.05rem",
    lineHeight: 1
  },
  estatisticaRotulo: {
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    fontWeight: 700
  },
  busca: {
    marginLeft: "auto",
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper
    }
  },
  botaoAtualizar: {
    textTransform: "none",
    borderRadius: 12,
    fontWeight: 700,
    padding: "7px 16px",
    border: `1px solid ${borda}`,
    "&:hover .MuiSvgIcon-root": { animation: "iaGirar .7s ease" }
  },

  // ---------- cartão do banco ----------
  card: {
    ...cartaoAstro(theme, "#94a3b8"),
    borderRadius: 20,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2.25),
    transition: "transform .2s ease, border-color .2s ease",
    // banco desligado: linha do topo cinza
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: escuro ? "rgba(255,255,255,0.12)" : "#dfe7e2",
      transition: "background .3s ease"
    },
    // hover leve: só sobe um pouco (sem sombra animada, sem brilho)
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: escuro ? "0 8px 24px rgba(0,0,0,.3)" : "0 4px 14px rgba(16,40,28,.06)"
    }
  },
  // banco ativo: linha verde + brilho verde suave
  cardOnline: {
    borderColor: escuro ? "rgba(74,222,128,0.32) !important" : "#bfe5cc !important",
    backgroundImage: escuro
      ? "radial-gradient(280px 140px at 100% 0%, rgba(34,197,94,0.16), transparent 70%)"
      : "radial-gradient(280px 140px at 100% 0%, rgba(34,197,94,0.09), transparent 70%)",
    "&::before": {
      background: "linear-gradient(90deg, #16a34a, #4ade80, #16a34a)"
    }
  },
  cardDesligado: {
    "& $avatar": { filter: "grayscale(1)", opacity: 0.6 },
    "& $nome": { opacity: 0.75 }
  },
  linhaTopo: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  avatar: {
    flexShrink: 0,
    width: 46,
    height: 46,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "0.95rem",
    letterSpacing: ".02em",
    color: "#fff",
    background: "linear-gradient(135deg, #0f5f35 0%, #1E9855 55%, #4ade80 120%)",
    boxShadow: "0 8px 18px rgba(30,152,85,.28), inset 0 1px 0 rgba(255,255,255,.25)",
  },
  nome: {
    fontWeight: 800,
    fontSize: "1rem",
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "opacity .3s ease"
  },
  compartilha: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: "0.72rem",
    minHeight: "1.1rem",
    "& .MuiSvgIcon-root": { fontSize: 13 }
  },
  // pílula de status com bolinha
  status: {
    marginLeft: "auto",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: ".02em",
    transition: "all .3s ease"
  },
  statusOn: {
    backgroundColor: escuro ? "rgba(34,197,94,.16)" : "#e7f8ee",
    color: escuro ? "#4ade80" : "#15803d"
  },
  statusOff: {
    backgroundColor: escuro ? "rgba(255,255,255,.07)" : "#f1f4f2",
    color: "#8a9a91"
  },
  bolinha: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "currentColor"
  },
  // anel que pulsa num pseudo-elemento (transform/opacity, leve)
  bolinhaViva: {
    position: "relative",
    backgroundColor: "#22c55e",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      backgroundColor: "#22c55e",
      animation: "iaPulso 2.4s ease-out infinite"
    }
  },

  // faixa "Consulta da IA" com switch
  faixaConsulta: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 8px 10px 14px",
    borderRadius: 14,
    border: `1px dashed ${borda}`,
    backgroundColor: escuro ? "rgba(255,255,255,.025)" : "#f8fbf9",
    transition: "background-color .3s ease, border-color .3s ease"
  },
  faixaConsultaOn: {
    borderStyle: "solid",
    borderColor: escuro ? "rgba(74,222,128,.25)" : "#cdebd8",
    backgroundColor: escuro ? "rgba(34,197,94,.07)" : "#f0faf4"
  },
  rotuloControle: {
    fontSize: "0.8rem",
    fontWeight: 700,
    lineHeight: 1.2
  },
  rastro: {
    display: "block",
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
    marginTop: 2
  },

  // ações em "ladrilhos"
  acoes: {
    marginTop: "auto",
    paddingTop: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8
  },
  acao: {
    position: "relative",
    textTransform: "none",
    borderRadius: 12,
    padding: "9px 4px 7px",
    border: `1px solid ${borda}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: "0.74rem",
    fontWeight: 700,
    minWidth: 0,
    transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
    "& .MuiButton-label": {
      display: "flex",
      flexDirection: "column",
      gap: 3
    },
    "& .MuiSvgIcon-root": {
      fontSize: 19,
      color: "#1E9855"
    },
    "&:hover": {
      backgroundColor: escuro ? "rgba(34,197,94,.12)" : "#ecf9f1",
      borderColor: escuro ? "rgba(74,222,128,.35)" : "#a7dcbc",
      color: escuro ? "#4ade80" : "#15803d"
    }
  },
  // ponto verde: banco já tem instruções
  marcaInstrucao: {
    position: "absolute",
    top: 6,
    right: 8,
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    boxShadow: "0 0 0 3px rgba(34,197,94,.18)"
  },

  tituloGrupo: {
    ...tituloSecaoAstro(theme),
    margin: theme.spacing(3.5, 0, 1.75)
  },
  contagemGrupo: {
    padding: "2px 9px",
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: 0,
    backgroundColor: escuro ? "rgba(34,197,94,.16)" : "#e7f8ee",
    color: escuro ? "#4ade80" : "#15803d"
  },

  // ---------- modais ----------
  campo: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: escuro ? "rgba(255,255,255,.03)" : "#fbfdfc",
      transition: "box-shadow .2s ease",
      "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(34,197,94,.14)" }
    }
  },
  dicaSegura: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 12,
    marginBottom: theme.spacing(2.5),
    fontSize: "0.78rem",
    color: escuro ? "#a7d7b9" : "#245b3c",
    backgroundColor: escuro ? "rgba(34,197,94,.08)" : "#effaf3",
    border: `1px solid ${escuro ? "rgba(74,222,128,.18)" : "#d3eedd"}`,
    "& .MuiSvgIcon-root": { fontSize: 20, color: "#22c55e" }
  },
  dialogoInstrucoes: {
    height: "80vh",
    maxHeight: 780
  },
  conteudoInstrucoes: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  legendaInstrucoes: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: theme.spacing(1.25),
    lineHeight: 1.5
  },
  campoInstrucoes: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    resize: "none",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: escuro ? "rgba(0,0,0,.2)" : "#fbfdfc",
    color: theme.palette.text.primary,
    fontFamily: "'JetBrains Mono', Consolas, monospace",
    fontSize: "0.85rem",
    lineHeight: 1.7,
    outline: "none",
    overflowY: "auto",
    transition: "border-color .2s ease, box-shadow .2s ease",
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.divider} transparent`,
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: 3
    },
    "&:hover": { borderColor: theme.palette.text.secondary },
    "&:focus": {
      borderColor: "#22c55e",
      boxShadow: "0 0 0 4px rgba(34,197,94,.14)"
    },
    "&::placeholder": { color: theme.palette.text.disabled }
  },
  operador: {
    border: `1px solid ${borda}`,
    borderRadius: 14,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    backgroundColor: escuro ? "rgba(255,255,255,.02)" : "#fbfdfc",
    animation: "iaSubir .3s ease backwards"
  },
  vazio: {
    padding: theme.spacing(7, 3),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  vazioIcone: {
    width: 64,
    height: 64,
    margin: "0 auto 14px",
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: escuro ? "rgba(34,197,94,.12)" : "#e7f8ee",
    color: "#22c55e",
    animation: "iaFlutuar 2.6s ease-in-out infinite",
    "& .MuiSvgIcon-root": { fontSize: 32 }
  },
  resultadoTeste: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 12,
    marginTop: theme.spacing(1.5),
    animation: "iaModalEntrar .35s cubic-bezier(0.22, 1, 0.36, 1) backwards",
    "& > .MuiSvgIcon-root": { animation: "iaIconePop .5s ease backwards" }
  },
  testeOk: {
    backgroundColor: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,.25)",
    color: escuro ? "#4ade80" : "#15803d"
  },
  testeFalha: {
    backgroundColor: "rgba(229,57,53,0.10)",
    border: "1px solid rgba(229,57,53,.22)",
    color: escuro ? "#ff8a80" : "#c62828"
  },
  testeNeutro: {
    backgroundColor: "rgba(120,120,120,0.10)"
  },
  detalheTeste: {
    display: "block",
    marginTop: 2,
    fontSize: "0.74rem",
    opacity: 0.85,
    wordBreak: "break-word"
  }
  };
});

// "C6 Bank" -> "C6", "Nossa Fintech CLT" -> "NF"
const iniciais = nome => {
  const palavras = String(nome || "")
    .replace(/\b(CLT|FGTS)\b/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (palavras.length === 0) return "?";
  // siglas curtas ficam inteiras: C6, V8, 3RN, HUB
  if (palavras[0].length <= 3) return palavras[0].toUpperCase();
  if (palavras.length === 1) return palavras[0].slice(0, 2).toUpperCase();
  return (palavras[0][0] + palavras[1][0]).toUpperCase();
};

const ResultadoTeste = ({ resultado, classes }) => {
  const estilo =
    resultado.ok === true
      ? classes.testeOk
      : resultado.ok === false
      ? classes.testeFalha
      : classes.testeNeutro;

  const Icone = resultado.ok === true ? CheckCircleOutlineIcon : ErrorOutlineIcon;

  return (
    <Box className={`${classes.resultadoTeste} ${estilo}`}>
      <Icone fontSize="small" />
      <Box>
        <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.8rem" }}>
          {resultado.mensagem}
          {resultado.segundos != null && ` (${resultado.segundos}s)`}
        </Typography>
        {resultado.detalhe && (
          <Typography variant="caption" className={classes.detalheTeste}>
            {resultado.detalhe}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const BankEnginePanel = () => {
  const classes = useStyles();

  const [carregando, setCarregando] = useState(true);
  const [bancos, setBancos] = useState([]);
  const [credenciais, setCredenciais] = useState({});
  const [motorOk, setMotorOk] = useState(null);

  const [grupoAberto, setGrupoAberto] = useState(null);
  const [valores, setValores] = useState({});
  // Como as credenciais estavam quando o formulario abriu. No salvar so vai
  // o que a pessoa realmente mudou: um formulario velho (aba aberta de antes,
  // ou resposta atrasada de outro banco) nao regrava mais um login antigo.
  const [originais, setOriginais] = useState({});
  const grupoPedido = useRef(null);
  const [revelados, setRevelados] = useState({});
  const [salvando, setSalvando] = useState(false);

  const [testando, setTestando] = useState(null);
  const [resultados, setResultados] = useState({});

  
  const [instrucoes, setInstrucoes] = useState({});
  const [bancoInstrucao, setBancoInstrucao] = useState(null);
  const [textoInstrucao, setTextoInstrucao] = useState("");
  const [salvandoInstrucao, setSalvandoInstrucao] = useState(false);

  const [busca, setBusca] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/ai/engine/banks");
      setBancos(data.bancos || []);
      setCredenciais(data.credenciais || {});
      setMotorOk(true);

      try {
        const { data: cfg } = await api.get("/ai/config");
        setInstrucoes(JSON.parse(cfg?.bankPrompts || "{}") || {});
      } catch (e) {
        setInstrucoes({});
      }
    } catch (err) {
      setMotorOk(false);
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  
  
  const formatarQuando = iso => {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (e) {
      return iso;
    }
  };

  const alternar = async (banco, campo, valor) => {
    const anterior = bancos;
    
    setBancos(bs => bs.map(b => (b.chave === banco.chave ? { ...b, [campo]: valor } : b)));

    try {
      await api.put(`/ai/engine/banks/${banco.chave}/online`, { online: valor });

      toast.success(
        valor
          ? `${banco.nome} ativado — a IA volta a consultar nele.`
          : `${banco.nome} desligado — a IA não consulta mais nele.`
      );
    } catch (err) {
      setBancos(anterior);
      toastError(err);
    }
  };

  const abrirInstrucoes = banco => {
    setBancoInstrucao(banco);
    setTextoInstrucao(instrucoes[banco.chave] || "");
  };

  const salvarInstrucoes = async () => {
    if (!bancoInstrucao) return;
    setSalvandoInstrucao(true);

    
    const atualizado = { ...instrucoes };
    const texto = textoInstrucao.trim();
    if (texto) atualizado[bancoInstrucao.chave] = texto;
    else delete atualizado[bancoInstrucao.chave];

    try {
      await api.put("/ai/config", { bankPrompts: JSON.stringify(atualizado) });
      setInstrucoes(atualizado);
      setBancoInstrucao(null);
      toast.success("Instruções salvas.");
    } catch (err) {
      toastError(err);
    } finally {
      setSalvandoInstrucao(false);
    }
  };

  const abrirCredenciais = async grupo => {
    grupoPedido.current = grupo;
    setGrupoAberto(grupo);
    setValores(null);
    setOriginais({});
    setRevelados({});
    try {
      const { data } = await api.get(`/ai/engine/credentials/${grupo}`);
      // Abriu outro banco enquanto este carregava: descarta a resposta.
      if (grupoPedido.current !== grupo) return;
      setValores(data || {});
      setOriginais(data || {});
    } catch (err) {
      if (grupoPedido.current !== grupo) return;
      toastError(err);
      setGrupoAberto(null);
    }
  };

  const revelar = async campo => {
    if (revelados[campo]) {
      setRevelados(r => ({ ...r, [campo]: false }));
      return;
    }
    try {
      const { data } = await api.get(`/ai/engine/credentials/${grupoAberto}?revelar=1`);
      setValores(v => ({ ...v, [campo]: data[campo] }));
      setOriginais(o => ({ ...o, [campo]: data[campo] }));
      setRevelados(r => ({ ...r, [campo]: true }));
    } catch (err) {
      toastError(err);
    }
  };

  const testar = async grupo => {
    setTestando(grupo);
    setResultados(r => ({ ...r, [grupo]: null }));
    try {
      const { data } = await api.post(`/ai/engine/credentials/${grupo}/test`);
      setResultados(r => ({ ...r, [grupo]: data }));
    } catch (err) {
      setResultados(r => ({
        ...r,
        [grupo]: { ok: false, mensagem: "O teste não chegou a rodar.", detalhe: err.message }
      }));
    } finally {
      setTestando(null);
    }
  };

  const salvar = async () => {
    const mudou = {};
    Object.keys(valores || {}).forEach(k => {
      if (JSON.stringify(valores[k]) !== JSON.stringify(originais[k])) mudou[k] = valores[k];
    });
    if (!Object.keys(mudou).length) {
      toast.info("Nada foi alterado.");
      setGrupoAberto(null);
      return;
    }
    setSalvando(true);
    try {
      await api.put(`/ai/engine/credentials/${grupoAberto}`, mudou);
      toast.success("Credenciais salvas. O próximo acesso já usa as novas.");
      setGrupoAberto(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const campoSimples = campo => {
    const ehSenha = campo.tipo === "senha";
    const escondido = ehSenha && !revelados[campo.nome];

    return (
      <Grid item xs={12} sm={6} key={campo.nome}>
        <TextField
          fullWidth
          className={classes.campo}
          variant="outlined"
          size="small"
          label={campo.rotulo}
          type={campo.tipo === "numero" ? "number" : "text"}
          value={valores[campo.nome] ?? ""}
          onChange={e =>
            setValores(v => ({
              ...v,
              [campo.nome]: campo.tipo === "numero" ? e.target.value : e.target.value
            }))
          }
          InputProps={
            ehSenha
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={escondido ? "Mostrar valor salvo" : "Esconder"} arrow>
                        <IconButton size="small" onClick={() => revelar(campo.nome)}>
                          {escondido ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }
              : undefined
          }
          helperText={
            ehSenha && valores[campo.nome] === MASCARA
              ? "Já preenchido. Digite por cima para trocar."
              : " "
          }
        />
      </Grid>
    );
  };

  const campoLista = campo => {
    const itens = valores[campo.nome] || [];

    const mudar = (indice, sub, valor) =>
      setValores(v => ({
        ...v,
        [campo.nome]: (v[campo.nome] || []).map((item, i) =>
          i === indice ? { ...item, [sub]: valor } : item
        )
      }));

    const adicionar = () =>
      setValores(v => ({
        ...v,
        [campo.nome]: [
          ...(v[campo.nome] || []),
          Object.fromEntries(campo.subcampos.map(s => [s.nome, ""]))
        ]
      }));

    const remover = indice =>
      setValores(v => ({
        ...v,
        [campo.nome]: (v[campo.nome] || []).filter((_, i) => i !== indice)
      }));

    return (
      <Grid item xs={12} key={campo.nome}>
        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 800 }}>
          {campo.rotulo}
        </Typography>

        {itens.map((item, indice) => (
          <Box key={indice} className={classes.operador}>
            <Grid container spacing={2} alignItems="center">
              {campo.subcampos.map(sub => (
                <Grid item xs={12} sm={3} key={sub.nome}>
                  <TextField
                    fullWidth
                    className={classes.campo}
                    variant="outlined"
                    size="small"
                    label={sub.rotulo}
                    type={sub.tipo === "numero" ? "number" : "text"}
                    value={item[sub.nome] ?? ""}
                    onChange={e => mudar(indice, sub.nome, e.target.value)}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={2}>
                <Button
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => remover(indice)}
                  style={{ textTransform: "none" }}
                >
                  Remover
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={adicionar}
          style={{ textTransform: "none", borderRadius: 10, borderStyle: "dashed", fontWeight: 700 }}
        >
          Adicionar operador
        </Button>
      </Grid>
    );
  };

  if (carregando) {
    return (
      <Box className={classes.vazio}>
        <Box className={classes.vazioIcone}>
          <AccountBalanceOutlinedIcon />
        </Box>
        <CircularProgress size={22} style={{ color: "#22c55e" }} />
        <Typography variant="body2" style={{ marginTop: 12, fontWeight: 600 }}>
          Falando com o motor de bancos...
        </Typography>
      </Box>
    );
  }

  if (motorOk === false) {
    return (
      <Box className={classes.vazio}>
        <Box className={classes.vazioIcone} style={{ color: "#e53935", backgroundColor: "rgba(229,57,53,.10)" }}>
          <CloudOffIcon />
        </Box>
        <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
          Motor de bancos fora do ar
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Confira se o serviço <code>bank-engine</code> subiu e se a variável
          ENGINE_API_KEY está definida no .env.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={carregar}
          style={{ marginTop: 18, textTransform: "none", borderRadius: 12, fontWeight: 700 }}
        >
          Tentar de novo
        </Button>
      </Box>
    );
  }

  const termo = busca.trim().toLowerCase();
  const porProduto = produto =>
    bancos.filter(b => b.produto === produto && (!termo || b.nome.toLowerCase().includes(termo)));
  const campoDoGrupo = grupoAberto ? credenciais[grupoAberto] : null;
  const totalAtivos = bancos.filter(b => b.online).length;

  const estatisticas = [
    { rotulo: "Ativos", valor: totalAtivos, cor: "#22c55e", Icone: PowerSettingsNewIcon },
    { rotulo: "Desligados", valor: bancos.length - totalAtivos, cor: "#94a3b8", Icone: PowerSettingsNewIcon },
    { rotulo: "Integrações", valor: bancos.length, cor: "#0ea5e9", Icone: AccountBalanceOutlinedIcon }
  ];

  const cartaoBanco = (banco, indice) => (
    <Grid item xs={12} sm={6} md={4} key={banco.chave}>
      <Box
        className={`${classes.card} ${banco.online ? classes.cardOnline : classes.cardDesligado}`}
        style={{ animationDelay: `${Math.min(indice, 12) * 0.05}s` }}
      >
        <Box className={classes.linhaTopo}>
          <Box className={classes.avatar}>{iniciais(banco.nome)}</Box>
          <Box minWidth={0} flex={1}>
            <Typography className={classes.nome} title={banco.nome}>
              {banco.nome}
            </Typography>
            <Typography className={classes.compartilha} component="div">
              {banco.compartilhaCom.length > 0 ? (
                <>
                  <LinkIcon /> Mesmo login de {banco.compartilhaCom.join(", ")}
                </>
              ) : banco.produto === "fgts" ? (
                "Saque-aniversário FGTS"
              ) : (
                "Consignado CLT"
              )}
            </Typography>
          </Box>
          <span className={`${classes.status} ${banco.online ? classes.statusOn : classes.statusOff}`}>
            <span className={`${classes.bolinha} ${banco.online ? classes.bolinhaViva : ""}`} />
            {banco.online ? "Ativo" : "Desligado"}
          </span>
        </Box>

        <Box className={`${classes.faixaConsulta} ${banco.online ? classes.faixaConsultaOn : ""}`}>
          <Box flex={1} minWidth={0}>
            <span className={classes.rotuloControle}>
              {banco.online ? "IA consultando este banco" : "Fora da consulta da IA"}
            </span>
            {/* Quem mexeu e quando. Um banco fora da consulta vira
                discussao sem isto: o clique nao deixava rastro. */}
            {banco.onlineEm && (
              <span className={classes.rastro}>
                {banco.online ? "Ligado" : "Desligado"} em {formatarQuando(banco.onlineEm)}
                {banco.onlinePor ? ` por ${banco.onlinePor}` : ""}
              </span>
            )}
          </Box>
          <Switch
            color="primary"
            checked={banco.online}
            onChange={e => alternar(banco, "online", e.target.checked)}
          />
        </Box>

        <Box className={classes.acoes}>
          <Button className={classes.acao} onClick={() => abrirCredenciais(banco.credencial)}>
            <VpnKeyOutlinedIcon />
            Credenciais
          </Button>
          <Tooltip
            title="Bate no banco para ver se o login funciona. Não consulta cliente nem cria proposta."
            arrow
          >
            <span style={{ display: "flex" }}>
              <Button
                className={classes.acao}
                style={{ width: "100%" }}
                disabled={testando === banco.credencial}
                onClick={() => testar(banco.credencial)}
              >
                {testando === banco.credencial ? (
                  <CircularProgress size={19} style={{ color: "#22c55e" }} />
                ) : (
                  <PlayArrowIcon />
                )}
                {testando === banco.credencial ? "Testando" : "Testar"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              instrucoes[banco.chave]
                ? "Instruções configuradas. Clique para editar o passo a passo deste banco."
                : "Passo a passo que a assistente segue para simular e digitar a proposta neste banco. Só entra na conversa depois que o cliente escolhe esta opção."
            }
            arrow
          >
            <Button className={classes.acao} onClick={() => abrirInstrucoes(banco)}>
              {instrucoes[banco.chave] && <span className={classes.marcaInstrucao} />}
              <DescriptionOutlinedIcon />
              Instruções
            </Button>
          </Tooltip>
        </Box>

        {resultados[banco.credencial] && (
          <ResultadoTeste resultado={resultados[banco.credencial]} classes={classes} />
        )}
      </Box>
    </Grid>
  );

  const renderGrupo = (titulo, lista) =>
    lista.length > 0 && (
      <>
        <Typography className={classes.tituloGrupo} component="div">
          {titulo}
          <span className={classes.contagemGrupo}>
            {lista.filter(b => b.online).length}/{lista.length} ativos
          </span>
        </Typography>
        <Grid container spacing={2} alignItems="stretch">
          {lista.map(cartaoBanco)}
        </Grid>
      </>
    );

  const listaClt = porProduto("clt");
  const listaFgts = porProduto("fgts");

  return (
    <Box>
      <Box className={classes.cabecalho}>
        <Box>
          <Typography className={classes.tituloPainel}>Bancos</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Credenciais e disponibilidade de cada integração. Banco desligado sai
            da consulta da IA na hora, seja o cliente de CLT ou de FGTS.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={carregar} className={classes.botaoAtualizar}>
          Atualizar
        </Button>
      </Box>

      <Box className={classes.barraResumo}>
        {estatisticas.map((e, i) => (
          <Box key={e.rotulo} className={classes.estatistica} style={{ animationDelay: `${i * 0.07}s` }}>
            <Box
              className={classes.estatisticaIcone}
              style={{ backgroundColor: `${e.cor}22`, color: e.cor }}
            >
              <e.Icone />
            </Box>
            <Box>
              <div className={classes.estatisticaNumero}>{e.valor}</div>
              <div className={classes.estatisticaRotulo}>{e.rotulo}</div>
            </Box>
          </Box>
        ))}
        <TextField
          className={classes.busca}
          size="small"
          variant="outlined"
          placeholder="Buscar banco..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#22c55e" }} />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {renderGrupo("Consignado CLT", listaClt)}
      {renderGrupo("FGTS", listaFgts)}

      {termo && listaClt.length === 0 && listaFgts.length === 0 && (
        <Box className={classes.vazio}>
          <Box className={classes.vazioIcone}>
            <SearchIcon />
          </Box>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Nenhum banco encontrado para "{busca}".
          </Typography>
        </Box>
      )}

      <ModalAstro
        open={!!grupoAberto}
        onClose={() => setGrupoAberto(null)}
        maxWidth="md"
        icone={<VpnKeyOutlinedIcon />}
        titulo={`Credenciais — ${campoDoGrupo?.rotulo || grupoAberto || ""}`}
        subtitulo="Acesso que a IA usa para consultar e digitar neste banco."
        acoes={
          <>
            <Button
              onClick={() => testar(grupoAberto)}
              disabled={testando === grupoAberto}
              startIcon={
                testando === grupoAberto ? <CircularProgress size={14} /> : <PlayArrowIcon />
              }
              style={{ marginRight: "auto" }}
            >
              {testando === grupoAberto ? "Testando..." : "Testar conexão"}
            </Button>
            <Button onClick={() => setGrupoAberto(null)}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={salvar}
              disabled={salvando || !valores}
              startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
            >
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </>
        }
      >
        {!valores ? (
          <Box textAlign="center" py={5}>
            <CircularProgress size={26} style={{ color: "#22c55e" }} />
          </Box>
        ) : (
          <>
            <Box className={classes.dicaSegura}>
              <LockOutlinedIcon />
              Os valores ficam guardados no servidor. Campos sensíveis aparecem
              mascarados — use o olho para conferir.
            </Box>
            <Grid container spacing={2}>
              {(campoDoGrupo?.campos || []).map(campo =>
                campo.tipo === "lista_credenciais" ? campoLista(campo) : campoSimples(campo)
              )}

              {resultados[grupoAberto] && (
                <Grid item xs={12}>
                  <ResultadoTeste resultado={resultados[grupoAberto]} classes={classes} />
                </Grid>
              )}
            </Grid>
          </>
        )}
      </ModalAstro>

      <ModalAstro
        open={!!bancoInstrucao}
        onClose={() => setBancoInstrucao(null)}
        maxWidth="md"
        icone={<DescriptionOutlinedIcon />}
        titulo={`Instruções — ${bancoInstrucao?.nome || ""}`}
        subtitulo="Como a assistente simula e digita a proposta neste banco. Entra na conversa só depois que o cliente escolhe esta opção, e vale acima do roteiro geral."
        paperClassName={classes.dialogoInstrucoes}
        conteudoClassName={classes.conteudoInstrucoes}
        acoes={
          <>
            <Button
              onClick={() => setTextoInstrucao("")}
              disabled={salvandoInstrucao || !textoInstrucao}
              startIcon={<DeleteOutlineIcon />}
              style={{ marginRight: "auto" }}
            >
              Limpar
            </Button>
            <Button onClick={() => setBancoInstrucao(null)}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={salvarInstrucoes}
              disabled={salvandoInstrucao}
              startIcon={salvandoInstrucao ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
            >
              {salvandoInstrucao ? "Salvando..." : "Salvar instruções"}
            </Button>
          </>
        }
      >
        <textarea
          className={classes.campoInstrucoes}
          spellCheck={false}
          placeholder={
            "Ex.:\n\n1. Pergunte quanto o cliente quer receber.\n" +
            "2. Chame simular_tabelas_facta com esse valor.\n" +
            "3. Apresente a tabela aprovada (prazo, parcela, valor liberado).\n" +
            "4. ..."
          }
          value={textoInstrucao}
          onChange={e => setTextoInstrucao(e.target.value)}
        />
        <Typography variant="caption" color="textSecondary" component="div" className={classes.legendaInstrucoes}>
          <span>
            Só o operacional deste banco — tom de voz fica em Configurações
            Globais, dúvidas de cliente nas Perguntas Frequentes.
          </span>
          <span style={{ flexShrink: 0, fontWeight: 700 }}>
            {textoInstrucao.length.toLocaleString("pt-BR")} caracteres
          </span>
        </Typography>
      </ModalAstro>
    </Box>
  );
};

export default BankEnginePanel;
